import { loadMotionGlyphs, type MotionGlyph } from "@/lib/generation-motion/glyphs"
import { motionDotGrid, motionSeed } from "@/lib/generation-motion/plan"
import {
  MotionHost,
  type MotionMessage,
  type MotionSceneConfig,
  type MotionStatus,
  type MotionTheme,
} from "@/lib/generation-motion/scene"

/**
 * Главный поток: создаёт канвасы, следит за размером/видимостью/темой и шлёт команды в рендерер.
 * Рендерер — один Web Worker на вкладку (OffscreenCanvas); без поддержки — тот же код на главном потоке.
 */

export type GenerationMotionOptions = {
  seedKey: string
  status: MotionStatus
  onMedia: boolean
  /** Видимый масштаб (зум доски): на отдалении канвас рисуется в меньшем разрешении. */
  resolutionScale: number
  /** Нижняя полоса (CSS px) под подпись статуса — буквы её обходят. */
  reserveBottom?: number
}

export type GenerationMotionHandle = {
  update: (options: GenerationMotionOptions) => void
  finish: () => void
  dispose: () => void
}

/** Потолок пикселей одного канваса — крупные превью не раздувают кадр. */
const MAX_CANVAS_PIXELS = 900_000
const MAX_DPR = 2
const VISIBILITY_MARGIN = "120px"

type Entry = {
  id: number
  host: HTMLElement
  canvas: HTMLCanvasElement | null
  options: GenerationMotionOptions
  width: number
  height: number
}

function currentTheme(): MotionTheme {
  return document.documentElement.classList.contains("light") ? "light" : "dark"
}

function sceneConfig(options: GenerationMotionOptions): MotionSceneConfig {
  return {
    seed: motionSeed(options.seedKey),
    status: options.status,
    onMedia: options.onMedia,
    reserveBottom: options.reserveBottom ?? 0,
  }
}

function canvasScale(width: number, height: number, resolutionScale: number) {
  const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1) * resolutionScale
  const capped = Math.min(dpr, Math.sqrt(MAX_CANVAS_PIXELS / Math.max(1, width * height)))
  const scale = Math.max(0.5, Math.min(MAX_DPR, capped))
  const { step } = motionDotGrid(width, height)
  return Math.max(1, Math.round(step * scale)) / step
}

class GenerationMotionEngine {
  private worker: Worker | null = null
  private mainHost: MotionHost | null = null
  private entries = new Map<number, Entry>()
  private byHost = new WeakMap<Element, Entry>()
  private nextId = 1
  private glyphs: MotionGlyph[] | null = null
  private resizeObserver: ResizeObserver
  private intersectionObserver: IntersectionObserver
  private reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)")

  constructor() {
    this.startBackend()
    this.resizeObserver = new ResizeObserver((items) => {
      for (const item of items) {
        const entry = this.byHost.get(item.target)
        if (!entry) continue
        entry.width = Math.round(item.contentRect.width)
        entry.height = Math.round(item.contentRect.height)
        this.sendSize(entry)
      }
    })
    this.intersectionObserver = new IntersectionObserver(
      (items) => {
        for (const item of items) {
          const entry = this.byHost.get(item.target)
          if (entry) this.send({ type: "visible", id: entry.id, visible: item.isIntersecting })
        }
      },
      { rootMargin: VISIBILITY_MARGIN },
    )
    new MutationObserver(() => this.send({ type: "theme", theme: currentTheme() })).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })
    this.reducedQuery.addEventListener("change", () =>
      this.send({ type: "reduced", reduced: this.reducedQuery.matches }),
    )
    document.addEventListener("visibilitychange", () => this.send({ type: "page", hidden: document.hidden }))
    void loadMotionGlyphs().then((glyphs) => {
      this.glyphs = glyphs
      this.send({ type: "glyphs", glyphs })
    })
  }

  private startBackend() {
    const canTransfer =
      typeof OffscreenCanvas !== "undefined" && "transferControlToOffscreen" in HTMLCanvasElement.prototype
    if (canTransfer) {
      try {
        this.worker = new Worker(new URL("./generation-motion.worker.ts", import.meta.url), { type: "module" })
        this.worker.addEventListener("error", () => this.fallbackToMain())
      } catch {
        this.worker = null
      }
    }
    if (!this.worker) this.useMainHost()
    this.sendGlobals()
  }

  private useMainHost() {
    this.mainHost = new MotionHost(
      (cb) => window.requestAnimationFrame(cb),
      () => performance.now(),
    )
  }

  private sendGlobals() {
    this.send({ type: "theme", theme: currentTheme() })
    this.send({ type: "reduced", reduced: this.reducedQuery.matches })
    this.send({ type: "page", hidden: document.hidden })
    if (this.glyphs) this.send({ type: "glyphs", glyphs: this.glyphs })
  }

  /** Воркер не поднялся (CSP, dev-прокси) — пересоздаём канвасы и рисуем на главном потоке. */
  private fallbackToMain() {
    if (!this.worker) return
    this.worker.terminate()
    this.worker = null
    this.useMainHost()
    this.sendGlobals()
    for (const entry of this.entries.values()) {
      this.attachCanvas(entry)
      this.sendSize(entry)
      this.intersectionObserver.unobserve(entry.host)
      this.intersectionObserver.observe(entry.host)
    }
  }

  private send(msg: MotionMessage, transfer?: Transferable[]) {
    if (this.worker) this.worker.postMessage(msg, transfer ?? [])
    else this.mainHost?.handle(msg)
  }

  private attachCanvas(entry: Entry) {
    entry.canvas?.remove()
    const canvas = document.createElement("canvas")
    canvas.setAttribute("aria-hidden", "true")
    entry.host.appendChild(canvas)
    entry.canvas = canvas
    const config = sceneConfig(entry.options)
    if (this.worker) {
      const offscreen = canvas.transferControlToOffscreen()
      this.send({ type: "add", id: entry.id, canvas: offscreen, config }, [offscreen])
    } else {
      this.send({ type: "add", id: entry.id, canvas, config })
    }
  }

  private sendSize(entry: Entry) {
    if (entry.width <= 0 || entry.height <= 0) return
    this.send({
      type: "size",
      id: entry.id,
      width: entry.width,
      height: entry.height,
      scale: canvasScale(entry.width, entry.height, entry.options.resolutionScale),
    })
  }

  register(host: HTMLElement, options: GenerationMotionOptions): GenerationMotionHandle {
    const entry: Entry = { id: this.nextId++, host, canvas: null, options, width: 0, height: 0 }
    this.entries.set(entry.id, entry)
    this.byHost.set(host, entry)
    this.attachCanvas(entry)
    this.resizeObserver.observe(host)
    this.intersectionObserver.observe(host)

    return {
      update: (next) => {
        const rescale = next.resolutionScale !== entry.options.resolutionScale
        entry.options = next
        this.send({ type: "config", id: entry.id, config: sceneConfig(next) })
        if (rescale) this.sendSize(entry)
      },
      finish: () => this.send({ type: "finish", id: entry.id }),
      dispose: () => {
        this.resizeObserver.unobserve(host)
        this.intersectionObserver.unobserve(host)
        this.byHost.delete(host)
        this.entries.delete(entry.id)
        this.send({ type: "remove", id: entry.id })
        entry.canvas?.remove()
        entry.canvas = null
      },
    }
  }
}

let engine: GenerationMotionEngine | null = null

export function registerGenerationMotion(host: HTMLElement, options: GenerationMotionOptions) {
  engine ??= new GenerationMotionEngine()
  return engine.register(host, options)
}
